<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'

// Plugins only have to declare the props they actually use. The host
// also passes `file` (normalized) and `parsed` (matches() return value)
// — set inheritAttrs: false so they don't fall through onto the root.
defineOptions({ inheritAttrs: false })

const props = defineProps({
  content: { type: [Blob, String, Object], default: null },
  file: { type: Object, default: () => ({}) },
})

// The peek arrives as a Blob (the plugin declares `peekResponseType:
// 'blob'`), so it has to become an object URL before an <img> can show
// it. That URL owns memory until revoked — hence the watch/unmount
// bookkeeping below, which keeps the lifetime inside the component
// instead of leaking into the host's modal-close handler.
const previewUrl = ref(null)

const setSource = (content) => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = null
  }
  if (content instanceof Blob) {
    previewUrl.value = URL.createObjectURL(content)
  }
}

watch(() => props.content, setSource, { immediate: true })
onBeforeUnmount(() => setSource(null))

const altText = computed(() => props.file?.name || 'Image preview')
</script>

<template>
  <div class="image-preview">
    <img v-if="previewUrl" :src="previewUrl" :alt="altText" />
    <p v-else class="has-text-centered py-4 has-text-grey">
      Image content unavailable
    </p>
  </div>
</template>

<style lang="scss" scoped>
.image-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 0;
  overflow: auto;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}
</style>
